/**
 * 🔧 [Phase 56.5 — 2026-07-09] Dialog "Mettre à jour les dates d'équipements"
 * extrait depuis CalendrierProduction.jsx (bloc legacy ~L1534-1592).
 *
 * Rôle : quand les dates_chantier d'un BT changent, on demande à l'utilisatrice
 * de recocher les jours où chaque équipement sera utilisé. Pattern grille bouton
 * toggle par équipement × dates.
 *
 * Contrat :
 *   - `data` : payload showEquipementsDates = { equipements: [{...}], newDates: [dateStr] }
 *   - `onCancel()` : ferme sans persister
 *   - `onToggleDate(eqIdx, dateStr)` : bascule la présence d'une date sur un équipement
 *   - `onConfirm()` : persiste les dates choisies (delegate au parent)
 *
 * Aucun changement fonctionnel — pur split JSX.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export default function EquipementsDatesDialog({ data, onCancel, onToggleDate, onConfirm }) {
  if (!data) return null;
  return (
    <Dialog open={!!data} onOpenChange={() => onCancel?.()}>
      <DialogContent className="w-[600px] max-w-[95vw] max-h-[80vh] overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle>Mettre à jour les dates d'équipements</DialogTitle>
          <DialogDescription>
            Les dates du contrat ont changé. Sélectionnez les jours où chaque équipement sera utilisé.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {data.equipements.map((eq, eqIdx) => (
            <div key={eq.equipement_id} className="border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">{eq.emoji}</span>
                <span className="font-medium">{eq.equipement_nom}</span>
                <Badge variant="outline" className="ml-auto">
                  {(eq.dates_utilisation || []).length} jour{(eq.dates_utilisation || []).length > 1 ? 's' : ''}
                </Badge>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {data.newDates.map(dateStr => {
                  const isSelected = (eq.dates_utilisation || []).includes(dateStr);
                  const dateObj = new Date(dateStr + 'T12:00:00');
                  const dayName = dateObj.toLocaleDateString('fr-CA', { weekday: 'short' });
                  const dayNum = dateObj.getDate();
                  const monthName = dateObj.toLocaleDateString('fr-CA', { month: 'short' });
                  return (
                    <button
                      key={dateStr}
                      onClick={() => onToggleDate?.(eqIdx, dateStr)}
                      className={`p-2 rounded-lg border text-center transition-all ${
                        isSelected
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="text-xs uppercase">{dayName}</div>
                      <div className="text-lg font-bold">{dayNum}</div>
                      <div className="text-xs">{monthName}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-2">
          <Button variant="outline" onClick={() => onCancel?.()}>
            Annuler
          </Button>
          <Button onClick={() => onConfirm?.()} className="bg-blue-600 hover:bg-blue-700 text-white">
            Confirmer les dates
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
